import { hasResource } from '@ctrl/db';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { OrderItemsEditor } from '@/components/orders/order-items-editor';
import { useDetailTitle } from '@/components/page-header';
import { PayDialog } from '@/components/pos/pay-dialog';
import { EmptyState, ReadOnlyNotice, SectionCard } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { adminApi, type OrderQuote } from '@/lib/admin-api';
import { useAuth } from '@/lib/auth';
import type { OrderFormLine } from '@/lib/order-form';
import { getQueueEntry, listOpenSessions } from '@/lib/pos';
import { listLocations } from '@/lib/settings/locations';

/**
 * The register.
 *
 * Everything a counter sale needs and nothing it does not: the basket, the
 * price as the server computes it, and one button that takes cash. The
 * customer, the store and the drawer are all decided before this screen
 * opens, which is why there is no form here -- just a basket and a total.
 *
 * The total shown is the same quote the sale is placed against; the server
 * recomputes it anyway before handing back change, so a stale screen costs
 * a refused tender rather than a mispriced sale.
 */
export const Route = createFileRoute('/queue/register/$entryId')({
  component: RegisterPage,
});

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

function RegisterPage() {
  const { entryId } = useParams({ from: '/queue/register/$entryId' });
  const { activeAccountId, claims } = useAuth();
  const accountId = activeAccountId ?? '';
  const navigate = useNavigate();

  const canSell = accountId ? hasResource(claims, accountId, 'payments') : false;

  const [lines, setLines] = useState<OrderFormLine[]>([]);
  const [paying, setPaying] = useState(false);

  const entry = useQuery({
    queryKey: ['queue-entry', accountId, entryId],
    queryFn: () => getQueueEntry(accountId, entryId),
    enabled: Boolean(accountId),
  });

  const locations = useQuery({
    queryKey: ['locations', accountId],
    queryFn: () => listLocations(accountId),
    enabled: Boolean(accountId),
  });

  const sessions = useQuery({
    queryKey: ['register-sessions', accountId],
    queryFn: () => listOpenSessions(accountId),
    enabled: Boolean(accountId),
  });

  useDetailTitle(entry.data?.customer?.name ?? 'Register');

  const locationId =
    entry.data?.locationId ??
    locations.data?.find((location) => location.type === 'store')?.id ??
    locations.data?.[0]?.id ??
    null;

  // The drawer at this store, if one is open. A sale without it is still a
  // sale; it is simply not in anybody's till.
  const session = (sessions.data ?? []).find(
    (candidate) => !candidate.locationId || candidate.locationId === locationId,
  );

  const customerType = entry.data?.customer?.customerType ?? 'recreational';

  const basket = useMemo(
    () =>
      lines.length && entry.data?.customer
        ? {
            customerId: entry.data.customer.id,
            locationId,
            customerType,
            fulfillmentType: 'walk_in',
            address: null,
            lines: lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity })),
            promoCode: null,
            manualDiscountCents: 0,
            manualDiscountNote: null,
            tipCents: 0,
            paymentProvider: 'cash',
          }
        : null,
    [lines, entry.data?.customer, locationId, customerType],
  );

  const basketKey = JSON.stringify(basket);
  const [debouncedKey, setDebouncedKey] = useState(basketKey);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedKey(basketKey), 250);

    return () => clearTimeout(timer);
  }, [basketKey]);

  const quote = useQuery({
    queryKey: ['pos-quote', accountId, debouncedKey],
    queryFn: () => adminApi.orders.quote(accountId, JSON.parse(debouncedKey)),
    enabled: debouncedKey !== 'null',
    placeholderData: (previous) => previous,
  });

  const quoted: OrderQuote | null = basket ? (quote.data ?? null) : null;

  const sell = useMutation({
    mutationFn: (tenderedCents: number) => {
      if (!entry.data?.customer) throw new Error('This entry has no customer on it.');

      return adminApi.pos.sell(accountId, {
        queueEntryId: entryId,
        customerId: entry.data.customer.id,
        customerType,
        locationId,
        registerSessionId: session?.id ?? null,
        lines: lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity })),
        promoCode: null,
        manualDiscountCents: 0,
        manualDiscountNote: null,
        tenderedCents,
        // One key per basket per entry: a double-click rings one sale.
        idempotencyKey: `pos:${entryId}:${lines
          .map((line) => `${line.variantId}x${line.quantity}`)
          .sort()
          .join(',')}`,
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (entry.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!entry.data) {
    return (
      <EmptyState>
        No such queue entry.{' '}
        <Link to="/queue" className="underline">
          Back to the queue
        </Link>
      </EmptyState>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          {session ? (
            <Badge variant="secondary">{session.registerName} open</Badge>
          ) : (
            <Badge variant="outline">No drawer open</Badge>
          )}
        </div>
      </div>

      {!canSell && <ReadOnlyNotice permission="payments.manage" />}

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {entry.data.customer?.name ?? 'Walk-in'}
        </h1>
        <p className="text-muted-foreground text-sm">
          {customerType === 'recreational' ? 'Recreational' : 'Medical'}
          {entry.data.notes ? ` · ${entry.data.notes}` : ''}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <SectionCard title="Basket" description="Search the catalog and add what they are buying.">
          <OrderItemsEditor
            accountId={accountId}
            locationId={locationId}
            customerType={customerType}
            lines={lines}
            onChange={(update) => setLines(update)}
            disabled={!canSell}
          />
        </SectionCard>

        <SectionCard title="Total" description="Priced by the server, including tax and any fees.">
          {quoted ? (
            <dl className="space-y-2 text-sm">
              <Row label="Subtotal" value={usd(quoted.pricing.subtotalCents)} />
              {quoted.pricing.discountCents > 0 && (
                <Row label="Discounts" value={`−${usd(quoted.pricing.discountCents)}`} />
              )}
              {quoted.fees.map((fee) => (
                <Row key={fee.name} label={fee.name} value={usd(fee.amountCents)} />
              ))}
              {quoted.pricing.taxLines.map((tax) => (
                <Row key={tax.kind} label={tax.name} value={usd(tax.amountCents)} />
              ))}
              <div className="flex items-baseline justify-between gap-4 border-t pt-3">
                <dt className="font-semibold">Total</dt>
                <dd className="text-xl font-semibold tabular-nums">
                  {usd(quoted.pricing.totalCents)}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-muted-foreground text-sm">Add something to price it.</p>
          )}

          <Button
            type="button"
            className="w-full"
            disabled={!canSell || !quoted || !lines.length}
            onClick={() => setPaying(true)}
          >
            Take payment
          </Button>
        </SectionCard>
      </div>

      {quoted && entry.data.customer && (
        <PayDialog
          open={paying}
          onOpenChange={setPaying}
          totalCents={quoted.pricing.totalCents}
          customerName={entry.data.customer.name}
          customerEmail={entry.data.customer.email}
          lines={lines}
          quote={quoted}
          pending={sell.isPending}
          onPay={async (tenderedCents) => {
            const result = await sell.mutateAsync(tenderedCents);

            return result;
          }}
          onDone={() => navigate({ to: '/queue' })}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
